import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, catchError, debounceTime, filter, finalize, map, of, takeUntil, tap } from 'rxjs';

import { DynamicDialogRef } from 'primeng/dynamicdialog';

import { CurrentUserService, SpinnerService, navigateWithRefresh } from '../../../shared';
import { organizationChannelOptions } from '../../../cmg-1003/models/lookups';
import { DocumentUpload34Service } from '../../services/document-upload-3.4.service';
import { RouteApiService } from '../../../pipeline/stores/api/route-api.service';
import { Models } from '../../../pipeline/models/global.models';
import { SecurityProfileModel, SecurityProfileQuery } from '../../../loan-state/security-profile';
import { Select } from 'primeng/select';
import { CmgFormControlDomDirective } from '../../../shared/directives/cmg-form-control-dom.directive';
import { AsyncPipe } from '@angular/common';
import { Message } from 'primeng/message';
import { FileUploader34Component } from '../file-uploader-3.4/file-uploader-3.4.component';
import { Button, ButtonDirective, ButtonLabel } from 'primeng/button';
import { CmgBusyIndicatorComponent } from '../../../../../../../libs/ui/src/lib/components/cmg-busy-indicator/cmg-busy-indicator.component';

@Component({
  selector: 'cmg-clear2-ui-upload-3.4',
  templateUrl: './upload-3.4.component.html',
  styleUrls: ['./upload-3.4.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    Select,
    CmgFormControlDomDirective,
    Message,
    FileUploader34Component,
    Button,
    ButtonDirective,
    ButtonLabel,
    CmgBusyIndicatorComponent,
    AsyncPipe,
  ],
})
export class Upload34Component implements OnInit, OnDestroy {
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _formBuilder = inject(UntypedFormBuilder);
  private readonly _documentUpload34Service = inject(DocumentUpload34Service);
  private readonly _api = inject(RouteApiService);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _router = inject(Router);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);

  uploadForm: UntypedFormGroup;
  private _destroyed$: Subject<boolean> = new Subject<boolean>();
  organizationChannelOptions = organizationChannelOptions;
  uploadedFileForm: FormBuilder;
  uploadedFile: any;
  currentUser: any;
  loanDataBase64: any;
  newFileUploaded: any;
  selectedLoanOfficer?: Models.LoanOfficers;
  isFileUploaded = false;
  errorMessage: string = null;
  allLoanOfficers = signal<Models.LoanOfficers[]>([]);
  isLoading$ = this._spinnerService.count$.pipe(
    debounceTime(1),
    map((t) => t > 0),
  );

  loadingMessage$ = this._spinnerService.message$;

  constructor() {
    this.uploadForm = this._formBuilder.group(
      {
        loanOfficerName: [],
        originationChannel: [],
      },
      { updateOn: 'blur' },
    );
  }

  ngOnInit(): void {
    this._api.loanOfficers$.pipe(takeUntil(this._destroyed$)).subscribe((data: Models.LoanOfficers[]) => {
      if (data) {
        this.allLoanOfficers.set(data);
        this.uploadForm.get('loanOfficerName').setValue((this._securityProfileQuery.getActive() as SecurityProfileModel)?.userId);
      }
    });
    this._currentUserService
      .getCurrentUserInfo()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((userInfo) => {
        if (userInfo) {
          this.currentUser = userInfo;
        }
      });

    this.uploadForm
      .get('loanOfficerName')
      .valueChanges.pipe(
        filter((t) => !!t),
        takeUntil(this._destroyed$),
      )
      .subscribe(this._onLoanOfficerChanged);
  }

  filesAdded(file: any) {
    this.uploadedFile = {
      filename: file[0].file.name,
      documentTypeGUID: '',
      file: file[0].file,
    };
    this.errorMessage = null;
  }

  uploadfiles() {
    this.newFileUploaded = {
      desiredOriginationChannel: this.uploadForm.get('originationChannel').value,
      loanOfficerUserName: this.selectedLoanOfficer?.username,
      organizationCode: this.selectedLoanOfficer?.organizationCode,
      subPropState: 'test', //subPropState should be "test" or "train". Removing it will break the EPS code
    };

    this._getBase64String();
  }

  closeDialog() {
    this._dialogRef.close();
  }

  onClearUpload() {
    this.uploadedFile = null;
  }

  private readonly _onLoanOfficerChanged = (selectedLoanOfficer: any) => {
    this.selectedLoanOfficer = this.allLoanOfficers().find((lo) => lo.userId === selectedLoanOfficer);
  };

  private _getBase64String() {
    const securityProfile = this._securityProfileQuery.getActive();
    const reader = new FileReader();
    reader.readAsDataURL(this.uploadedFile.file);
    reader.onload = () => {
      this.loanDataBase64 = reader.result;

      const fileDetails = {
        loanDataBase64: reader.result,
        newFile: this.newFileUploaded,
      };
      const upload34Request = {
        userOrgProfileId: (securityProfile as SecurityProfileModel).userOrgProfileId,
        fileDetails: fileDetails,
      };
      this._spinnerService.show();
      this._documentUpload34Service
        .uploadDocuments(upload34Request)
        .pipe(
          tap((result) => {
            window.open(`${location.origin}/urla2/${result.newFile.fileDataID}`);
            navigateWithRefresh(this._router, '/pipeline', { state: { pipelineType: Models.PipelineType.AllLoans } });
            this.closeDialog();
          }),
          catchError((error) => {
            this.errorMessage = '<strong>' + this.uploadedFile?.filename + '</strong> - ';
            this.errorMessage += error?.error?.Errors
              ? this._updateErrorLogs(error.error)
              : (error?.error?.message ?? error?.error?.Detail ?? 'Validation exception issue: ' + error?.error);
            this.errorMessage += '<br>' + 'Please select a new file and try again.';
            this.onClearUpload();
            return of(error);
          }),
          finalize(() => {
            this._spinnerService.hide();
          }),
        )
        .subscribe();
    };
  }

  private readonly _updateErrorLogs = (error): string => {
    let errorMsg = '';

    const validationErrors = error.Errors;
    for (const key in validationErrors) {
      if (validationErrors && Array.isArray(validationErrors[key])) {
        validationErrors[key].forEach((e) => {
          errorMsg = errorMsg + e;
        });
      }
    }
    return errorMsg;
  };

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }
}
